import React, { useState, useEffect, useRef, useCallback } from "react"
import I18n from "@helper/i18nInstance"

import "./QuestionQuestion.scss"

import { useQuiz } from "@Quiz/context"
import ContainedImg from "@Quiz/Ui/ContainedImg"
import { useQuizCard } from "@Quiz/Ui/QuizCard"
import { QuestionHeader } from "@Quiz/Ui"
import Button from "@ui/Button"

const SHOW_ONLY_IMAGE_OR_VIDEO_FOR_MILLIS = 1000
const RECALC_INNER_HEIGHT_AFTER_MILLIS = 1100 // sync with CSS transition for question-text height

const AUTO_SHOW_ANSWERS_AFTER_MILLIS_WITH_IMAGE = SHOW_ONLY_IMAGE_OR_VIDEO_FOR_MILLIS + 6000
export const AUTO_SHOW_ANSWERS_AFTER_MILLIS_WITHOUT_IMAGE = 6000
const AUTO_SHOW_FADE_ALL_OUT_MILLIS = 500 // sync with CSS
const LONG_QUESTION_LENGTH = 150
const WAIT_LONGER_UNTIL_AUTOSHOW_FOR_LONG_QUESTION_MILLIS = 2500

const FADE_IN_ANSWER_BUTTON_AFTER_MILLIS_WITH_IMAGE = 2000
const FADE_IN_ANSWER_BUTTON_AFTER_MILLIS_WITHOUT_IMAGE = 1000

const VideoHolder: React.FC<{src: string}> = ({src}) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [showPlayButton, setShowPlayButton] = useState(true)

  const clickPlayButton = useCallback(() => {
    if (!videoRef.current) { return }
    videoRef.current.play()
    setShowPlayButton(false)
  }, [])

  useEffect(() => { // fix iPhone shows still-frame (?)
    if (!videoRef.current) { return }
    videoRef.current.muted = true
    videoRef.current.play().catch(() => videoRef.current && (videoRef.current.muted = false))
    window.setTimeout(() => {
      if (!videoRef.current) { return }
      videoRef.current.pause()
      videoRef.current.muted = false
    }, 1)
  }, [])

  return <div className="neo__questionQuestion__videoHolder">
    {showPlayButton &&
      <a onClick={clickPlayButton} className="neo__questionQuestion__videoPlayButtonHolder">
        <div className="neo__questionQuestion__videoPlayButtonHolder__backdrop" />
        <span><span>▶</span></span>
      </a>}
    <video ref={videoRef} className="neo__questionQuestion__video" controls={!showPlayButton} playsInline>
      <source src={src} type="video/mp4" />
    </video>
  </div>
}

const RawWithImageOrVideo: React.FC<React.PropsWithChildren<{quiz: Quiz.Api.Quiz, question: Quiz.Api.Question, fadeIn: boolean}>> = ({fadeIn, children, question}) => {
  const [ showTextElements, setShowTextElements ] = useState(!fadeIn)
  const [ textElementsHeight, setTextElementsHeight ] = useState(-1)
  const textElementsHeightRef = useRef(textElementsHeight)
  textElementsHeightRef.current = textElementsHeight
  const { recalcInnerHeight } = useQuizCard()
  const recalcInnerHeightRef = useRef(recalcInnerHeight)
  recalcInnerHeightRef.current = recalcInnerHeight
  const textElementsRef = useRef<HTMLDivElement>(null)
  const fadeInRef = useRef(fadeIn)

  useEffect(() => { // --------------- ONCE -> if "fadeIn" show textual description after some time
    if (!fadeInRef.current) { return }

    const timeout = window.setTimeout(() => {
      setShowTextElements(true)
    }, SHOW_ONLY_IMAGE_OR_VIDEO_FOR_MILLIS)

    return () => { // cleanup
      if (timeout) { window.clearTimeout(timeout) }
    }
  }, [])

  useEffect(() => { // on mount: if "fadeIn" cause recalcInnerHeight after animation time, otherwise immediately
    let timeout: number
    let aniFrame: number
    if (fadeInRef.current) {
      timeout = window.setTimeout(() => recalcInnerHeightRef.current(), RECALC_INNER_HEIGHT_AFTER_MILLIS)
    } else {
      aniFrame = window.requestAnimationFrame(() => recalcInnerHeightRef.current())
    }

    return () => { // cleanup
      if (timeout) { window.clearTimeout(timeout) }
      if (aniFrame) { window.cancelAnimationFrame(aniFrame) }
    }
  }, [])

  useEffect(() => { // --------------- Wait till text elements are there, then store their height
    let aniFrame: number
    const onFrame = () => {
      if (textElementsRef.current) {
        setTextElementsHeight(textElementsRef.current.clientHeight || 1)
      } else {
        aniFrame = window.requestAnimationFrame(onFrame)
      }
    }

    aniFrame = window.requestAnimationFrame(onFrame)

    return () => { // cleanup
      if (aniFrame) { window.cancelAnimationFrame(aniFrame) }
    }
  }, [])

  return (
    <>
      {question.image && <div className="neo__questionQuestion__imgWithCreditsHolder">
        <ContainedImg className={`neo__questionQuestion__img ${fadeIn ? "--fadeImageSize" : "--dontFadeImageSize" }`} src={question.image["1x"]} />
        {question.image_or_video_subtext && <div className="neo__questionQuestion__imgCredit">{question.image_or_video_subtext}</div>}
      </div>}
      {question.video && <div className="neo__questionQuestion__imgWithCreditsHolder">
        <VideoHolder src={question.video.media_url} />
        {question.image_or_video_subtext && <div className="neo__questionQuestion__imgCredit">{question.image_or_video_subtext}</div>}
      </div>}
      <div className={`neo__questionQuestion__textStuffHolder ${fadeIn ? "" : "--dontFade" }`} style={{height: `${Math.max(0, textElementsHeight)}px`}}>
        {showTextElements && <div ref={textElementsRef} className={"neo__questionQuestion__textStuffHolder__inner"}>
          {children}
        </div>}
      </div>
    </>
  )
}

const WithImageOrVideo: React.FC<React.PropsWithChildren<{fadeIn: boolean}>> = ({fadeIn, children}) => {
  const { quiz } = useQuiz()
  if (!quiz?.question) { return null }

  return <RawWithImageOrVideo fadeIn={fadeIn} question={quiz.question} quiz={quiz}>{children}</RawWithImageOrVideo>
}

const TextualDescription: React.FC<{fadeIn: boolean, question: Quiz.Api.Question, isLongQuestion: boolean}> = ({fadeIn, question, isLongQuestion}) => {
  const wrapperClassName = [
    fadeIn ? "neo__questionQuestion__wrapper--textFadeIn" : "",
    isLongQuestion ? "neo__questionQuestion__wrapper--longQuestion" : "",
    question.hint ? "neo__questionQuestion__wrapper--withHint" : "",
  ].join(" ")
  return (
    <div className={wrapperClassName}>
      {question.hint && <div className="neo__questionQuestion__hint" dangerouslySetInnerHTML={{__html: question.hint}} />}
      <div className="neo__questionQuestion__questionText" dangerouslySetInnerHTML={{__html: question.text}} />
    </div>
  )
}

const RawQuestionQuestion: React.FC<{showAgain: boolean, showAnswers(): void, quiz: Quiz.Api.Quiz, question: Quiz.Api.Question}> = ({showAgain, showAnswers, quiz, question}) => {
  const { recalcInnerHeight } = useQuizCard()
  const recalcInnerHeightRef = useRef(recalcInnerHeight)
  recalcInnerHeightRef.current = recalcInnerHeight
  const isLongQuestionRef = useRef(false)
  isLongQuestionRef.current = (question.text.length + (question.hint || "").length) >= LONG_QUESTION_LENGTH
  const showAnswersRef = useRef(showAnswers)
  showAnswersRef.current = showAnswers

  let fadeIn = true
  if (showAgain) { fadeIn = false }
  if (question.countdown && question.config) { fadeIn = false } // when countdown already started (e.g. after page refresh)
  const fadeInRef = useRef(fadeIn)
  fadeInRef.current = fadeIn

  const [ fadeAllOutToAutomaticallyShowAnswers, setFadeAllOutToAutomaticallyShowAnswers ] = useState(false)
  const [ fadeInAnswerButton, setFadeInAnswerButton ] = useState(!fadeIn)
  const automaticallyShowAnswers = !showAgain && !question.countdown && !question.video
  const withImageOrVideo = question.image || question.video
  const automaticallyShowAnswersRef = useRef(automaticallyShowAnswers)
  automaticallyShowAnswersRef.current = automaticallyShowAnswers
  const withImageOrVideoRef = useRef(withImageOrVideo)
  withImageOrVideoRef.current = withImageOrVideo

  useEffect(() => { // --------------- ONCE -> recalc cardheight (when switching back and forth between questions and answers)
    recalcInnerHeightRef.current()
  }, [])

  useEffect(() => { // --------------- ONCE -> Automatically show answers after some sec
    let timeout: number
    if (automaticallyShowAnswersRef.current) {
      timeout = window.setTimeout(
        () => {
          timeout = window.setTimeout(
            () => showAnswersRef.current(),
            AUTO_SHOW_FADE_ALL_OUT_MILLIS
          )
          setFadeAllOutToAutomaticallyShowAnswers(true)
        },
        (withImageOrVideoRef.current
          ? AUTO_SHOW_ANSWERS_AFTER_MILLIS_WITH_IMAGE
          : AUTO_SHOW_ANSWERS_AFTER_MILLIS_WITHOUT_IMAGE) +
        (isLongQuestionRef.current
          ? WAIT_LONGER_UNTIL_AUTOSHOW_FOR_LONG_QUESTION_MILLIS
          : 0))
    }

    return () => { // cleanup
      window.clearTimeout(timeout)
    }
  }, [])

  useEffect(() => { // --------------- ONCE -> Fade in Answer-Button
    let timeout: number
    if (fadeInRef.current) {
      timeout = window.setTimeout(
        () => setFadeInAnswerButton(true),
        withImageOrVideoRef.current
          ? FADE_IN_ANSWER_BUTTON_AFTER_MILLIS_WITH_IMAGE
          : FADE_IN_ANSWER_BUTTON_AFTER_MILLIS_WITHOUT_IMAGE)
    }

    return () => { // cleanup
      window.clearTimeout(timeout)
    }
  }, [])

  const bodyClassName = [
    "neo__questionQuestion__body",
    fadeAllOutToAutomaticallyShowAnswers ? "--fadeOutForAutoShowAnswers" : "",
    showAgain ? "--slideInBecauseShowAgain" : "",
  ].join(" ")

  return (
    <div className="neo__questionQuestion" data-testid="quiz-question-question">
      <div className="neo__questionQuestion__head"><QuestionHeader meta={quiz.meta} /></div>
      <div className={bodyClassName}>
        {withImageOrVideo
          ? <WithImageOrVideo fadeIn={fadeIn}>
              <TextualDescription isLongQuestion={isLongQuestionRef.current} fadeIn={false/* Wrapper is already fadingIn*/} question={question} />
            </WithImageOrVideo>
          : <TextualDescription isLongQuestion={isLongQuestionRef.current} fadeIn={fadeIn} question={question} />}
      </div>
      {showAgain
        ? <div className={`neo__questionQuestion__foot ${fadeInAnswerButton ? "--show" : "--hide"}`}>
            <Button onClick={showAnswers}>{I18n.t("quiz.question.btn_back_to_answers")}</Button>
          </div>
        : <div className={`neo__questionQuestion__foot ${fadeInAnswerButton ? "--show" : "--hide"}`}>
            <Button onClick={showAnswers}>{I18n.t("quiz.question.btn_answer")}</Button>
          </div>}
    </div>
  )
}

const QuestionQuestion: React.FC<{showAgain: boolean, showAnswers(): void}> = (props) => {
  const { quiz } = useQuiz()
  if (!quiz?.question) { return null }

  return <RawQuestionQuestion quiz={quiz} question={quiz.question} {...props} />
}

export default QuestionQuestion
